import React, { useEffect, useRef, useState } from "react";
import { Lock, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Admin unlock dialog.
 *
 * Two-factor admin gate:
 *   1. The current account email must be in ADMIN_EMAILS (allow-list)
 *   2. The user must enter the correct ADMIN_PASSCODE
 *
 * Customers (not on the allow-list) see a polite "not available" state instead
 * of any developer surface.
 */
export default function AdminUnlockDialog({ open, onOpenChange, onUnlocked }) {
  const { user, isAdminEligible, refresh } = useAuth();
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPasscode("");
      setError("");
      // Tiny delay so the input is in the DOM when we focus it
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!passcode.trim()) {
      setError("Enter the admin passcode to continue.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/unlock", { passcode: passcode.trim() });
      toast.success("Admin mode unlocked");
      await refresh();
      onUnlocked?.();
      onOpenChange(false);
    } catch (e2) {
      const detail = e2?.response?.data?.detail || "Could not unlock admin mode.";
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-md"
        data-testid="admin-unlock-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-[color:var(--brand-primary)]" />
            Admin / Developer Access
          </DialogTitle>
        </DialogHeader>

        {!isAdminEligible ? (
          // ---------- NOT ELIGIBLE ----------
          <div className="space-y-3 mt-1">
            <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--text-2)] leading-relaxed flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-[color:var(--accent-warm)] mt-0.5 shrink-0" />
              <div>
                <div className="text-[color:var(--text-1)] font-medium mb-1">
                  Admin access isn't enabled for this account.
                </div>
                You're signed in as{" "}
                <code className="rounded bg-[color:var(--bg-0)] px-1 py-0.5 text-[11px]">
                  {user?.email || "(unknown)"}
                </code>
                . If you're a platform admin, sign in with your admin email and try again.
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="admin-unlock-close"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          // ---------- ELIGIBLE -> SHOW PASSCODE FORM ----------
          <form onSubmit={submit} className="space-y-4 mt-1">
            <div className="text-xs text-[color:var(--text-2)] leading-relaxed">
              Signed in as{" "}
              <code className="rounded bg-[color:var(--bg-0)] px-1 py-0.5 text-[11px]">
                {user?.email}
              </code>
              . Enter your admin passcode to unlock developer setup tools (App IDs, redirect URIs, test
              connections). This stays active for the rest of your session.
            </div>

            <div>
              <label
                htmlFor="admin-passcode"
                className="block text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mb-1.5"
              >
                Admin passcode
              </label>
              <Input
                id="admin-passcode"
                ref={inputRef}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={12}
                placeholder="••••"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                disabled={busy}
                className="bg-[color:var(--bg-0)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] tracking-[0.4em] text-center text-lg"
                data-testid="admin-passcode-input"
              />
              {error ? (
                <div className="text-[11px] text-[color:var(--danger)] mt-2" data-testid="admin-unlock-error">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--border-subtle)]">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                data-testid="admin-unlock-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || !passcode}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="admin-unlock-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Unlock admin
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
